import React, { useEffect, useState } from "react";
import { Printer, FileText, AlertTriangle, CheckCircle, TrendingDown } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";

const STAGE_FROM_EGFR = (egfr) => {
  if (egfr >= 90) return "Stage 1";
  if (egfr >= 60) return "Stage 2";
  if (egfr >= 45) return "Stage 3a";
  if (egfr >= 30) return "Stage 3b";
  if (egfr >= 15) return "Stage 4";
  return "Stage 5";
};

const ACR_LABEL = (acr) => {
  if (acr < 30) return "Normal to mildly increased";
  if (acr <= 300) return "Moderately increased";
  return "Severely increased";
};

const MONTH_LABEL = (isoDate) => {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  } catch {
    return isoDate;
  }
};

export default function DoctorSummary() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [egfrList, setEgfrList] = useState([]);
  const [acrList, setAcrList] = useState([]);
  const [meds, setMeds] = useState([]);
  const [kfre, setKfre] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [sharedScorecards, setSharedScorecards] = useState([]);

  useEffect(() => {
    (async () => {
      const [profRes, egfrRes, acrRes, medsRes, agendaRes] = await Promise.all([
        api.get("/patient/profile"),
        api.get("/tracker/egfr"),
        api.get("/tracker/acr"),
        api.get("/meds"),
        api.get("/summary/agenda"),
      ]);
      setProfile(profRes.data);
      setEgfrList(egfrRes.data);
      setAcrList(acrRes.data);
      setMeds(medsRes.data);
      setQuestions(agendaRes.data.questions || []);
      setSharedScorecards(agendaRes.data.shared_scorecards || []);

      if (egfrRes.data.length && acrRes.data.length) {
        const lastE = egfrRes.data[egfrRes.data.length - 1].egfr;
        const lastA = acrRes.data[acrRes.data.length - 1].acr;
        const k = await api.post("/calc/kfre", {
          age: profRes.data.age,
          sex: profRes.data.sex,
          egfr: lastE,
          acr: lastA,
        });
        setKfre(k.data);
      }
      setLoading(false);
    })();
  }, []);

  const handlePrint = () => window.print();

  const lastEgfr = egfrList[egfrList.length - 1];
  const lastAcr = acrList[acrList.length - 1];

  // Build 6-month trend series (align eGFR and ACR by date approximately)
  const trends = egfrList.slice(-6).map((r) => {
    const nearest = acrList.reduce((best, a) => {
      if (!best) return a;
      const d1 = Math.abs(new Date(a.date) - new Date(r.date));
      const d2 = Math.abs(new Date(best.date) - new Date(r.date));
      return d1 < d2 ? a : best;
    }, null);
    return {
      date: MONTH_LABEL(r.date),
      egfr: r.egfr,
      acr: nearest ? nearest.acr : null,
    };
  });

  const generatedDate = new Date().toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-[calc(100vh-180px)]" data-testid="doctor-summary-page">
      {/* Non-printable action bar */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden">
        <div>
          <div className="ros-label">Clinical export</div>
          <h2 className="font-heading text-2xl mt-1">Nephrology visit report</h2>
          <p className="text-xs text-[#A3ACA7] mt-1">
            One-page snapshot to hand to your nephrologist.
          </p>
        </div>
        <button
          onClick={handlePrint}
          data-testid="print-summary-btn"
          className="flex items-center gap-2 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black px-5 py-2.5 rounded-full font-semibold transition-colors"
        >
          <Printer size={18} strokeWidth={2.5} /> Print / Save PDF
        </button>
      </div>

      {loading ? (
        <div className="max-w-4xl mx-auto ros-card text-center text-[#A3ACA7] py-16">
          Preparing your report…
        </div>
      ) : (
        <div
          id="doctor-summary-printable"
          className="max-w-4xl mx-auto bg-white text-black p-8 rounded-2xl shadow-2xl print:shadow-none print:p-0 print:rounded-none"
          data-testid="summary-printable"
        >
          {/* Header */}
          <div className="border-b-2 border-slate-800 pb-4 mb-6 flex justify-between items-end">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#4ADE80] flex items-center justify-center">
                  <span className="text-black font-black text-sm">R</span>
                </div>
                <h2 className="text-3xl font-black uppercase tracking-tight text-slate-900">
                  Med Nexus Patient Summary
                </h2>
              </div>
              <p className="text-slate-600 mt-1 text-sm">Generated: {generatedDate}</p>
            </div>
            <div className="text-right">
              <h3 className="text-xl font-bold text-slate-900">{profile?.name}</h3>
              <p className="text-sm text-slate-600">
                {profile?.sex === "female" ? "F" : "M"} · Age {profile?.age}
              </p>
              <p className="text-sm text-slate-600">ID: RNL-{profile?.id?.slice(-4).toUpperCase() || "DEMO"}</p>
            </div>
          </div>

          {/* Top-level metrics */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="border border-slate-300 p-4 rounded-lg bg-slate-50">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Current eGFR</div>
              <div className="text-4xl font-black text-slate-900 flex items-baseline gap-2 mt-1">
                {lastEgfr ? lastEgfr.egfr.toFixed(1) : "—"}
                <span className="text-xs font-normal text-slate-500">mL/min/1.73m²</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {lastEgfr ? `${STAGE_FROM_EGFR(lastEgfr.egfr)} CKD · CKD-EPI 2021` : ""}
              </div>
              {lastEgfr && (
                <div className="text-xs text-slate-500 mt-0.5">As of {lastEgfr.date}</div>
              )}
            </div>

            <div className="border border-slate-300 p-4 rounded-lg bg-slate-50">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Urine ACR</div>
              <div className="text-4xl font-black text-slate-900 flex items-baseline gap-2 mt-1">
                {lastAcr ? lastAcr.acr : "—"}
                <span className="text-xs font-normal text-slate-500">mg/g</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {lastAcr ? ACR_LABEL(lastAcr.acr) : ""}
              </div>
              {lastAcr && (
                <div className="text-xs text-slate-500 mt-0.5">As of {lastAcr.date}</div>
              )}
            </div>

            <div className="border border-slate-300 p-4 rounded-lg bg-slate-50">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Tangri KFRE Risk</div>
              <div className="text-base font-bold text-slate-900 mt-2">
                2-Year:{" "}
                <span className={kfre && kfre.risk_2yr >= 5 ? "text-red-600" : "text-slate-900"}>
                  {kfre ? `${kfre.risk_2yr.toFixed(2)}%` : "—"}
                </span>
              </div>
              <div className="text-base font-bold text-slate-900">
                5-Year:{" "}
                <span className={kfre && kfre.risk_5yr >= 10 ? "text-red-600" : "text-slate-900"}>
                  {kfre ? `${kfre.risk_5yr.toFixed(2)}%` : "—"}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Risk of ESRD / Dialysis</div>
            </div>
          </div>

          {/* Trend */}
          <div className="mb-8">
            <h3 className="text-lg font-bold border-b border-slate-200 pb-2 mb-4 flex items-center gap-2 text-slate-900">
              <TrendingDown size={20} /> eGFR Trajectory
            </h3>
            <div className="h-52 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#475569" }} tickLine={false} axisLine={false} />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fontSize: 12, fill: "#475569" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <ReferenceLine y={45} yAxisId="left" stroke="#94a3b8" strokeDasharray="3 3"
                                 label={{ value: "Stage 3a threshold", fill: "#64748b", fontSize: 10, position: "insideTopRight" }} />
                  <ReferenceLine y={30} yAxisId="left" stroke="#f87171" strokeDasharray="3 3"
                                 label={{ value: "Stage 3b threshold", fill: "#f87171", fontSize: 10, position: "insideBottomRight" }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="egfr"
                    stroke="#0f172a"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    name="eGFR"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Medications */}
          <div className="mb-8">
            <h3 className="text-lg font-bold border-b border-slate-200 pb-2 mb-4 flex items-center gap-2 text-slate-900">
              <AlertTriangle size={20} /> Medication Safety Review
            </h3>
            {meds.length === 0 ? (
              <div className="text-sm text-slate-500">No medications on file.</div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-xs uppercase">
                    <th className="p-2 font-bold">Medication</th>
                    <th className="p-2 font-bold">Dose</th>
                    <th className="p-2 font-bold">System Flag</th>
                    <th className="p-2 font-bold">Med Nexus Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {meds.map((med) => {
                    const isSafe = med.risk_level === "safe";
                    const isAvoid = med.risk_level === "avoid";
                    return (
                      <tr key={med.id} className="border-b border-slate-100 last:border-0" data-testid={`summary-med-${med.id}`}>
                        <td className="p-2 font-bold text-slate-900">{med.name}</td>
                        <td className="p-2 text-slate-700">{med.dose || "—"}</td>
                        <td className="p-2">
                          {isSafe ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-md font-semibold">
                              <CheckCircle size={14} /> Safe
                            </span>
                          ) : isAvoid ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-1 rounded-md font-semibold">
                              <AlertTriangle size={14} /> Avoid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-yellow-800 bg-yellow-100 px-2 py-1 rounded-md font-semibold">
                              <AlertTriangle size={14} /> Caution
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-slate-600">{med.reason || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Shared lab scorecards (patient-uploaded, translated by AI vision engine) */}
          {sharedScorecards.length > 0 && (
            <div className="mb-8" data-testid="summary-shared-scorecards">
              <h3 className="text-lg font-bold border-b border-slate-200 pb-2 mb-4 flex items-center gap-2 text-slate-900">
                <FileText size={20} /> Patient-Shared Lab Scorecards
                <span className="text-xs font-normal text-slate-500">({sharedScorecards.length} report{sharedScorecards.length > 1 ? "s" : ""})</span>
              </h3>
              {sharedScorecards.slice(0, 2).map((sc) => {
                const outOfRange = (sc.items || []).filter((it) => it.status === "high" || it.status === "low");
                const inRange = (sc.items || []).filter((it) => it.status === "normal");
                return (
                  <div key={sc.id} className="mb-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="font-bold text-slate-900">{sc.lab_name}</span>
                      <span className="text-xs text-slate-500">Reported {sc.report_date} · shared {sc.shared_at?.slice(0, 10)}</span>
                    </div>
                    {outOfRange.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1">Out of range</div>
                        <ul className="list-disc pl-5 text-sm text-slate-800 space-y-0.5">
                          {outOfRange.map((it) => (
                            <li key={it.name}>
                              <span className="font-semibold">{it.name.toUpperCase().replace("_", " ")}</span>: {it.value} {it.unit}
                              <span className="text-xs text-slate-500"> (range: {it.reference_range})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {inRange.length > 0 && (
                      <div className="mt-2 text-xs text-slate-600">
                        In range: {inRange.map((it) => it.name.replace("_", " ")).join(", ")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* AI-generated visit agenda */}
          <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl" data-testid="summary-agenda">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-slate-900">
              <FileText size={20} /> Patient Agenda
              <span className="text-xs font-normal text-slate-500">(AI-generated from your recent logs)</span>
            </h3>
            {questions.length === 0 ? (
              <div className="text-sm text-slate-500">Generating suggested questions…</div>
            ) : (
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                {questions.map((q, idx) => (
                  <li key={idx} className="font-medium">{q}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-slate-200 text-[10px] text-slate-500">
            Med Nexus · Educational summary. Not a substitute for professional medical advice. Please discuss all findings with your nephrologist.
          </div>
        </div>
      )}

      <style>{`
        @media print {
          html, body, #root { background-color: white !important; }
          .App { background: white !important; }
          aside, header, .print\\:hidden { display: none !important; }
          main { padding: 0 !important; }
          #doctor-summary-printable { box-shadow: none !important; padding: 0 !important; max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
